import React, { useEffect, useState } from 'react';
import axios from 'axios';
import '../ShippingServices/ShippingServiceForm.css';
import '../../../shared/StyledInput.css';
import RequiredMark from '../../../shared/RequiredMark';
import CancelButton from '../../../shared/CancelButton';
import SaveButton from '../../../shared/SaveButton';
import PriceCalculator from '../../../shared/PriceCalculator';
import NumberInput from '../../../shared/NumberInput';
import { isAustraliaPost, AUSPOST_OPTIONS } from '../../warehouse/packageShared';

function ShippingOptionForm({ initial, onSave, onCancel, loading }) {
  const [description, setDescription] = useState(initial?.description || '');
  const [shippingServiceId, setShippingServiceId] = useState(initial?.shipping_service_id ?? '');
  const [pricingMode, setPricingMode] = useState(initial?.pricing_mode || 'flat');
  const [flatFee, setFlatFee] = useState(initial?.flat_fee ?? '');
  const [scaledFreeThreshold, setScaledFreeThreshold] = useState(initial?.scaled_free_threshold ?? '');
  const [scaledIncrementSize, setScaledIncrementSize] = useState(initial?.scaled_increment_size ?? '');
  const [scaledRatePerIncrement, setScaledRatePerIncrement] = useState(initial?.scaled_rate_per_increment ?? '');
  const [scaledMaxCap, setScaledMaxCap] = useState(initial?.scaled_max_cap ?? '');
  const [valueBasis, setValueBasis] = useState(initial?.value_basis || 'quote_ex_gst');
  const [carrierOptionCode, setCarrierOptionCode] = useState(initial?.carrier_option_code || '');
  const [services, setServices] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get('/api/shipping-services', { params: { status: 'active' } })
      .then((res) => setServices(res.data.data || []))
      .catch(() => setServices([]));
  }, []);

  const serviceOptions = services.filter(
    (s) => s.is_active || String(s.id) === String(shippingServiceId)
  );

  // An Option on an Australia Post Service can be priced as one of
  // Australia Post's own at Ship (issue #316).
  const service = services.find((s) => String(s.id) === String(shippingServiceId));
  const isAusPost = !!service && isAustraliaPost(service.supplier?.name);

  const validate = () => {
    if (!description.trim()) return 'Description is required.';
    if (!shippingServiceId) return 'Service is required.';
    if (pricingMode === 'flat') {
      if (flatFee === '' || isNaN(Number(flatFee)) || Number(flatFee) < 0) return 'Fee Amount is required and must be a non-negative number.';
    } else {
      if ([scaledFreeThreshold, scaledIncrementSize, scaledRatePerIncrement, scaledMaxCap].some((v) => v === '' || isNaN(Number(v)) || Number(v) < 0)) {
        return 'Free Threshold, Increment Size, Rate per Increment, and Maximum Cap are all required and must be non-negative numbers.';
      }
    }
    return '';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const err = validate();
    if (err) return setError(err);
    setError('');
    onSave({
      description: description.trim(),
      shipping_service_id: Number(shippingServiceId),
      pricing_mode: pricingMode,
      flat_fee: pricingMode === 'flat' ? Number(flatFee) : null,
      scaled_free_threshold: pricingMode === 'scaled' ? Number(scaledFreeThreshold) : null,
      scaled_increment_size: pricingMode === 'scaled' ? Number(scaledIncrementSize) : null,
      scaled_rate_per_increment: pricingMode === 'scaled' ? Number(scaledRatePerIncrement) : null,
      scaled_max_cap: pricingMode === 'scaled' ? Number(scaledMaxCap) : null,
      value_basis: pricingMode === 'scaled' ? valueBasis : null,
      carrier_option_code: isAusPost && carrierOptionCode ? carrierOptionCode : null,
    });
  };

  return (
    <form className="shipping-service-form" onSubmit={handleSubmit} style={{ minWidth: 320 }}>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <div>
          <label>Description<RequiredMark /></label><br />
          <input
            className="styled-input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={150}
            placeholder="e.g., Signature on Delivery"
            style={{ width: 260 }}
          />
        </div>
        <div>
          <label>Service<RequiredMark /></label><br />
          <select
            className="styled-input"
            value={shippingServiceId}
            onChange={(e) => setShippingServiceId(e.target.value)}
            style={{ width: 220 }}
          >
            <option value="">(select)</option>
            {serviceOptions.map((s) => (
              <option key={s.id} value={s.id}>{s.description}</option>
            ))}
          </select>
        </div>
        {isAusPost && (
          <div>
            <label title="The Australia Post option this is priced as at Ship. Extra Cover is declared on what's in each package, at this Option's Value Basis (sell ex GST for a Flat Fee Option).">Australia Post Option</label><br />
            <select
              className="styled-input"
              value={carrierOptionCode}
              onChange={(e) => setCarrierOptionCode(e.target.value)}
              style={{ width: 220 }}
            >
              <option value="">(not linked)</option>
              {AUSPOST_OPTIONS.map((o) => <option key={o.code} value={o.code}>{o.name}</option>)}
            </select>
          </div>
        )}
      </div>

      <div style={{ marginTop: 14 }}>
        <label>Pricing Mode<RequiredMark /></label><br />
        <label style={{ marginRight: 16, fontWeight: 400 }}>
          <input type="radio" checked={pricingMode === 'flat'} onChange={() => setPricingMode('flat')} /> Flat Fee
        </label>
        <label style={{ fontWeight: 400 }}>
          <input type="radio" checked={pricingMode === 'scaled'} onChange={() => setPricingMode('scaled')} /> Scaled
        </label>
      </div>

      {pricingMode === 'flat' ? (
        <div style={{ marginTop: 14 }}>
          <label title="A fee of $0 represents an Option included free on this Service.">Fee Amount (ex GST)<RequiredMark /></label><br />
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <NumberInput
              minDecimals={2} maxDecimals={4}
              min="0"
              step="0.0001"
              value={flatFee}
              onChange={(e) => setFlatFee(e.target.value)}
              style={{ width: 140 }}
            />
            <PriceCalculator
              initialValue={flatFee === '' ? undefined : Number(flatFee)}
              onResult={(val) => setFlatFee(String(val))}
            />
          </div>
        </div>
      ) : (
        <div style={{ marginTop: 14, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          <div>
            <label title="Value below which this Option is free.">Free up to<RequiredMark /></label><br />
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <NumberInput
                minDecimals={2} maxDecimals={4}
                min="0"
                step="0.0001"
                value={scaledFreeThreshold}
                onChange={(e) => setScaledFreeThreshold(e.target.value)}
                style={{ width: 130 }}
              />
              <PriceCalculator
                initialValue={scaledFreeThreshold === '' ? undefined : Number(scaledFreeThreshold)}
                onResult={(val) => setScaledFreeThreshold(String(val))}
              />
            </div>
          </div>
          <div>
            <label title="The size of each increment the rate is charged per, e.g. 100 for &quot;per $100&quot;.">Per increment of<RequiredMark /></label><br />
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <NumberInput
                minDecimals={2} maxDecimals={4}
                min="0"
                step="0.0001"
                value={scaledIncrementSize}
                onChange={(e) => setScaledIncrementSize(e.target.value)}
                style={{ width: 130 }}
              />
              <PriceCalculator
                initialValue={scaledIncrementSize === '' ? undefined : Number(scaledIncrementSize)}
                onResult={(val) => setScaledIncrementSize(String(val))}
              />
            </div>
          </div>
          <div>
            <label title="The rate charged per increment above the free threshold.">Rate per increment<RequiredMark /></label><br />
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <NumberInput
                minDecimals={2} maxDecimals={4}
                min="0"
                step="0.0001"
                value={scaledRatePerIncrement}
                onChange={(e) => setScaledRatePerIncrement(e.target.value)}
                style={{ width: 130 }}
              />
              <PriceCalculator
                initialValue={scaledRatePerIncrement === '' ? undefined : Number(scaledRatePerIncrement)}
                onResult={(val) => setScaledRatePerIncrement(String(val))}
              />
            </div>
          </div>
          <div>
            <label title="The maximum value this Option can cover.">Maximum cap<RequiredMark /></label><br />
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <NumberInput
                minDecimals={2} maxDecimals={4}
                min="0"
                step="0.0001"
                value={scaledMaxCap}
                onChange={(e) => setScaledMaxCap(e.target.value)}
                style={{ width: 130 }}
              />
              <PriceCalculator
                initialValue={scaledMaxCap === '' ? undefined : Number(scaledMaxCap)}
                onResult={(val) => setScaledMaxCap(String(val))}
              />
            </div>
          </div>
          <div>
            <label title="What dollar figure the threshold/rate/cap above are measured against.">Value Basis<RequiredMark /></label><br />
            <select
              className="styled-input"
              value={valueBasis}
              onChange={(e) => setValueBasis(e.target.value)}
              style={{ width: 160 }}
            >
              <option value="quote_ex_gst">Quote ex-GST</option>
              <option value="cost_ex_gst">Cost ex-GST</option>
            </select>
          </div>
        </div>
      )}

      {error && <div style={{ color: 'red', marginTop: 8 }}>{error}</div>}

      <div style={{ marginTop: 12 }}>
        <SaveButton loading={loading} />
        <CancelButton onClick={onCancel} disabled={loading} style={{ marginLeft: 8 }} />
      </div>
    </form>
  );
}

export default ShippingOptionForm;
